import Waveform from './Waveform.jsx';
import formatClipTitle from '../../utils/formatClipTitle.js';

/**
 * A player's avatar IS their signature sound, shown as that sound's actual
 * waveform.
 *
 * The shape comes from the clip's measured peak envelope in the library
 * manifest, so two different sounds can never produce the same mark. Nothing
 * here is generated: a clip without peaks renders a flat "no waveform" state
 * rather than an invented shape that would look just as real.
 */

// Desaturated-but-rich. No primary rainbow, no neon.
const PALETTE = [
  '#3ACFCF', // teal
  '#E0A458', // amber
  '#E4738A', // rose
  '#9A8CF0', // violet
  '#6FB2E4', // slate blue
  '#8FC7A0', // sage
  '#D98E6A', // copper
  '#B9A2D9', // lilac
];

function hashString(str = '') {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

const SIZES = {
  sig: { w: 100, h: 26, bars: 24 },
  sm: { w: 64, h: 30, bars: 24 },
  md: { w: 88, h: 40, bars: 32 },
  lg: { w: 128, h: 54, bars: 44 },
  xl: { w: 176, h: 70, bars: 60 },
};

export function SoundAvatar({
  clip = null,
  seed = '',
  size = 'md',
  selected = false,
  playing = false,
  progress = null,
  onClick,
  title,
  showLabel = false,
  style = {},
}) {
  const dim = SIZES[size] || SIZES.md;
  const key = clip?.id || seed || 'sounddrift';
  const color = PALETTE[hashString(key) % PALETTE.length];
  const interactive = typeof onClick === 'function';

  return (
    <div
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); }
      } : undefined}
      title={title || (clip ? formatClipTitle(clip, 100) : undefined)}
      data-testid="sound-avatar"
      data-clip-id={clip?.id || ''}
      aria-label={clip ? `Signature sound: ${formatClipTitle(clip)}` : 'Player avatar'}
      style={{
        width: dim.w,
        padding: '6px 8px',
        borderRadius: 10,
        background: '#0d1620',
        border: `1px solid ${selected || playing ? color : '#26323f'}`,
        boxShadow: selected || playing ? `0 0 0 2px ${color}33` : 'none',
        cursor: interactive ? 'pointer' : 'default',
        transition: 'border-color 140ms ease, box-shadow 140ms ease',
        display: 'inline-flex',
        flexDirection: 'column',
        gap: 3,
        flexShrink: 0,
        boxSizing: 'border-box',
        ...style,
      }}
    >
      <Waveform
        peaks={clip?.peaks}
        seed={clip?.id || seed || 'sound_avatar'}
        bars={dim.bars}
        height={dim.h}
        color={color}
        progress={playing ? progress : null}
        showPlayhead={false}
        ariaLabel={clip ? `Waveform of ${formatClipTitle(clip)}` : 'Sound avatar waveform'}
      />
      {showLabel && clip && (
        <span style={{
          fontSize: '0.62rem',
          color: '#8fa0ad',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '100%',
          minWidth: 0,
        }}>
          {formatClipTitle(clip)}
        </span>
      )}
    </div>
  );
}

export default SoundAvatar;
